import './preview.scss';

import { useEffect, useState } from 'react';

import CameraButton from 'app/modules/shared/video/components/camera';
import MicrophoneButton from 'app/modules/shared/video/components/microphone';
import useIsMounted from 'app/hooks/useIsMounted';

import {
  selectIsLocalVideoEnabled,
  selectLocalPeer,
  useAVToggle,
  useHMSActions,
  useHMSStore,
  usePreviewJoin,
  useVideo,
} from "@100mslive/react-sdk";
import { useAuth } from 'app/context/auth-context';
import { useAppContext } from 'app/context/app-context';
import { InterviewSessionService } from 'app/services/InterviewSessionService';
import { Roles } from 'app/types';
import DeviceSettings from '../video/DeviceSettings';

type PreviewContainerProps = {
  width?: string | number;
  height?: string | number;
  roomId: string;
  onVideoStarted?: (value: boolean) => void;
  onAudioStarted?: (value: boolean) => void;
  isPreviewActive?: boolean;
  showDeviceSetting?: boolean;
  token?: string;
}

const PreviewContainer = (props: PreviewContainerProps) => {
  const { user } = useAuth();
  const { jobFair } = useAppContext()
  const localPeer = useHMSStore(selectLocalPeer);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const { isLocalAudioEnabled, isLocalVideoEnabled, toggleAudio, toggleVideo } = useAVToggle();
  const isMounted = useIsMounted('PreviewContainer');
  const [authToken, setAuthToken] = useState<string>(props.token || '');
  const hmsActions = useHMSActions();
  const [isPreviewStarted, setIsPreviewStarted] = useState<boolean>(false)
  useEffect(() => {
    if (props.token) {
      setAuthToken(props.token);
      return;
    }

    if (!jobFair?.id || !user?.id || !props.roomId) return;

    new InterviewSessionService().get100msAuthToken(jobFair.id, user.id.toString(), props.roomId, user.roles?.includes(Roles.Recruiter) ? 'interviewer' : 'candidate')
      .then(token =>
        setAuthToken(token)
      );

  }, [jobFair?.id, user?.id, props.roomId, props.token])

  const { videoRef } = useVideo({ trackId: localPeer?.videoTrack, attach: true });

  const { preview } = usePreviewJoin({
    token: authToken,
    initEndpoint: undefined,
    initialSettings: {
      isAudioMuted: false,
      isVideoMuted: false
    },
    captureNetworkQualityInPreview: true,
    handleError: (_, method) => {
      if (method === "preview") {
        console.log('error- preview');
      }
    },
  });

  useEffect(() => {
    if (props.onVideoStarted) {
      props.onVideoStarted(isVideoOn);
    }
  }, [isVideoOn])

  useEffect(() => {
    if (props.onAudioStarted) {
      props.onAudioStarted(isLocalAudioEnabled);
    }

  }, [isLocalAudioEnabled])

  useEffect(() => {
    if (authToken === '') return;

    if (props.isPreviewActive) {
      setIsPreviewStarted(true);
      preview();
    }
    if (isPreviewStarted && !props.isPreviewActive) {
      // Stop media tracks before leaving room to prevent abort errors
      hmsActions.setLocalVideoEnabled(false);
      hmsActions.setLocalAudioEnabled(false);
      hmsActions.leave().finally(() => {
        setIsPreviewStarted(false);
      })
    }
  }, [authToken, props.isPreviewActive, isPreviewStarted]);

  useEffect(() => {
    return () => {
      // Explicitly stop tracks and leave on unmount
      async function cleanup() {
        try {
          await hmsActions.setLocalVideoEnabled(false);
          await hmsActions.setLocalAudioEnabled(false);
        } catch (e) {
          console.error("Error disabling tracks on preview unmount", e);
        }
        await hmsActions.leave();
      }
      cleanup();
    }
  }, [hmsActions]);

  return (
    <>
      <div className="video-preview" style={{ width: props.width, height: props.height }}>
        <video className='video-canvas' style={{ height: props.height }} autoPlay muted playsInline controls={false} ref={videoRef} />
        <div className="controlPan">
          <div className="userThumb"></div>
          <div className="controls">
            <MicrophoneButton className="audio"
              isMuted={!isLocalAudioEnabled}
              onMicrophoneClick={() => { if (toggleAudio) toggleAudio(); }}
            />
            <CameraButton className="camera"
              isStartedVideo={isLocalVideoEnabled}
              onCameraClick={() => { if (toggleVideo) toggleVideo(); }}
              onSwitchCamera={() => { }}
              cameraList={[]}
              activeCamera={undefined}
            />
          </div>
          <div className="extraControls"></div>
        </div>
      </div>
      {props.showDeviceSetting && (
        <div className="mt-5 mb-5 d-flex border border-success">
          <DeviceSettings></DeviceSettings>
        </div>
      )}
    </>
  );
}

export default PreviewContainer;
